/**
 * Backend tenant-branding API — read + publish hooks.
 *
 * `useBranding()` wraps GET /v1/admin/branding. Visible to admin /
 * super_admin / compliance.
 *
 * `usePublishBranding()` wraps PATCH /v1/admin/branding. Admin /
 * super_admin only — compliance can read but not publish.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';
import type { RoleLabelOverrides } from '../labels';

export interface TenantBrandingView {
  firmName?: string;
  tagline?: string;
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  fontPair?: string;
  tone?: string;
  supportEmail?: string;
  customDomain?: string;
  /** Multi-member access gate (TenantDoc sibling): when true, a member added
   *  to a trust gets read access immediately but must be verified by the firm
   *  before they can sign, upload, or make changes. Off by default. */
  requireVerifiedBeforeActing?: boolean;
  updatedAt?: string;
  updatedByUserId?: string;
}

/** Client Model B — the firm's self-signup / who-pays configuration. NOT part of
 *  the branding overlay: the backend routes it to the sibling
 *  `TenantDoc.openSignup` (see `handlers/admin-branding-patch.ts`), which is what
 *  `branding-by-slug` publishes on the firm's branded signup page and what
 *  `auth/self-join` stamps onto each joining client. BOTH fields are required
 *  whenever `openSignup` is sent — the backend schema is `.strict()` and rejects
 *  a half-config rather than leaving one behind.
 *
 *  ⚠ `billingOwner` only takes effect for clients who arrive through the firm's
 *  branded self-signup, which `self-join` refuses unless `enabled` is true. With
 *  `enabled: false` the value is a recorded preference, not a live rule. */
export interface OpenSignupConfig {
  /** May clients sign themselves up on the firm's branded page? */
  enabled: boolean;
  /** Who carries the subscription for a client who joins that way. */
  billingOwner: 'firm' | 'client';
}

export interface BrandingResponse {
  branding: TenantBrandingView;
  /** The firm's self-signup / who-pays record. Returned by BOTH verbs since
   *  2026-08-23 — the GET projects it when the tenant has one (it used to project
   *  the branding overlay only), and the PATCH echoes it back when the request
   *  carried one.
   *
   *  ABSENT means "never asked", which is a real state: a firm that skipped the
   *  payment step has no record, and that is not the same as `enabled: false`
   *  with some assumed payer. The Branding Studio's self-signup card reads it
   *  and, when it is absent, asks both halves rather than guessing one. */
  openSignup?: OpenSignupConfig;
  /** Firm-chosen role TITLES. A TenantDoc sibling, not part of the theming
   *  overlay — the GET always projects it (`{}` when nothing is renamed), the
   *  PATCH echoes it back only when the request carried one. */
  roleLabels?: RoleLabelOverrides;
  /** The firm's own role NAMES with their server-minted ids. The GET always
   *  projects it (`[]` when the firm runs the four canonical chairs only); the
   *  PATCH echoes it back when the request carried one. See
   *  {@link CustomRoleView}. */
  customRoles?: CustomRoleView[];
  correlationId: string;
}

/**
 * A role the firm invented, bound to one canonical permission set.
 *
 * DISPLAY ALIAS, NOT A NEW ROLE. `base` is the canonical token every
 * authorization decision reads; `label` is only the firm's word for it. A
 * member holding "Trust Officer" is, to `lib/authz/*`, a `compliance` user —
 * the console renders a different noun and nothing else changes.
 */
export interface CustomRoleView {
  /** Server-minted (`cr_` + 12 hex). Never invent one client-side — the backend
   *  rejects an unknown shape and re-mints an id it has not issued. */
  id: string;
  label: string;
  base: 'admin' | 'compliance' | 'rm' | 'director';
}

/** Exported for the admin-dashboard prefetch — same key, same fetcher. */
export const BRANDING_ME_QUERY_KEY = ['branding', 'me'] as const;

export function fetchBranding(signal?: AbortSignal) {
  return api<BrandingResponse>('/api/v1/admin/branding', { signal });
}

/**
 * `enabled` exists so a screen can decline the read for a role the server
 * refuses it to (backend `isTenantAdminOrCompliance`: not `rm`). A 403 from an
 * unconditional call is a request the screen knew would fail; the persona kit
 * caught it on every /admin page an `rm` opened (4 Oct 2026). Gate with
 * `canReadFirmSettings(role)` from lib/authz/firm-capabilities.
 */
export function useBranding(opts: { enabled?: boolean } = {}) {
  return useQuery<BrandingResponse>({
    queryKey: BRANDING_ME_QUERY_KEY,
    queryFn: ({ signal }) => fetchBranding(signal),
    enabled: opts.enabled ?? true,
  });
}

/**
 * Operator-pinned branding read (F11) — same GET /v1/admin/branding, but forced
 * onto the operator's OWN Supabase session (`useOperatorSession: true`). The
 * firm console (Branding studio) uses this so it always reads the FIRM tenant's
 * branding, never a lingering act-as CLIENT tenant's overlay if a firm-user
 * lands there straight off an act-as portal.
 *
 * Cached under a distinct suffix key (`['branding','me','operator']`) so it can
 * never collide with the act-as-following `useBranding()` read — yet it still
 * sits UNDER the `['branding']` prefix, so `usePublishBranding()`'s
 * `invalidateQueries({ queryKey: ['branding'] })` (a prefix match) refreshes
 * this query after a publish too.
 */
export function useOperatorBranding(opts: { enabled?: boolean } = {}) {
  return useQuery<BrandingResponse>({
    queryKey: ['branding', 'me', 'operator'],
    queryFn: ({ signal }) =>
      api<BrandingResponse>('/api/v1/admin/branding', { signal, useOperatorSession: true }),
    // Same reason as `useBranding`: declined, not attempted, for a role the
    // server refuses the read to.
    enabled: opts.enabled ?? true,
  });
}

export type PublishBrandingRequest = Partial<
  Omit<TenantBrandingView, 'updatedAt' | 'updatedByUserId'>
> & {
  /** Access-control config, carried on the same PATCH. Send it whole or not at
   *  all — see {@link OpenSignupConfig}. */
  openSignup?: OpenSignupConfig;
  /** Firm-chosen role TITLES. PARTIAL by design: send only the keys that
   *  changed, and an EMPTY STRING to clear one back to its canonical Elreign
   *  name. Keys you omit are left exactly as they are. */
  roleLabels?: RoleLabelOverrides;
  /** The firm's own role NAMES. WHOLE-LIST, unlike `roleLabels` above: send
   *  every role the firm should end up with, because there is no delete verb in
   *  a PATCH of a list. An entry keeping its `id` is an EDIT (everyone already
   *  holding it is re-titled); an entry with no `id` is new and the server mints
   *  one. An empty array clears them all. */
  customRoles?: { id?: string; label: string; base: CustomRoleView['base'] }[];
};

export function usePublishBranding() {
  const qc = useQueryClient();
  return useMutation<BrandingResponse, ApiError, PublishBrandingRequest>({
    mutationFn: (body) =>
      api<BrandingResponse>('/api/v1/admin/branding', {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['branding'] });
      // This PATCH also writes TenantDoc siblings that ride /v1/tenants/me —
      // `roleLabels` above all. Without these two the firm's new role titles
      // sat behind a 30s stale window on every screen except the one that saved
      // them, which reads as "it didn't work". `['tenants']` covers the
      // act-as-following read; `['operator','tenant-me']` is a deliberately
      // isolated key (see lib/api/operator-identity.ts) and does NOT fall under
      // that prefix, so the console sidebar needs its own invalidation.
      void qc.invalidateQueries({ queryKey: ['tenants', 'me'] });
      void qc.invalidateQueries({ queryKey: ['operator', 'tenant-me'] });
    },
  });
}
